import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { addComment } from '@/api/comments';
import { errorMessage } from '@/api/errors';
import type { OrganizationSummary } from '@/api/orgs';
import { useCreateProject } from '@/api/projects';

export interface CreateProjectModalProps {
  opened: boolean;
  onClose: () => void;
  /** Organizations the caller may create in; a single one is preselected and hidden. */
  organizations: Pick<OrganizationSummary, 'public_id' | 'name'>[];
  defaultCrs?: string;
  /** Staff see, and may write, a first internal comment on the new project. */
  showInternal?: boolean;
  /** Where to go after creating; defaults to the client project page. */
  navigateTo?: (projectId: string) => void;
}

export function CreateProjectModal({ opened, onClose, organizations, defaultCrs, showInternal, navigateTo }: CreateProjectModalProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateProject();
  const qc = useQueryClient();
  const [org, setOrg] = useState<string | null>(organizations[0]?.public_id ?? null);
  const [name, setName] = useState('');
  const [crs, setCrs] = useState(defaultCrs ?? '');
  const [acq, setAcq] = useState('');
  const [acqEnd, setAcqEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName('');
    setAcq('');
    setAcqEnd('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const organization = org ?? organizations[0]?.public_id;
    if (!organization) return;
    setError(null);
    try {
      const p = await create.mutateAsync({
        organization,
        name,
        crs: crs || undefined,
        acquisition_date: acq || null,
        acquisition_period_end: acqEnd || null,
      });
      // A second request: the project stands even if the comment fails, so say so and go on.
      if (showInternal && notes.trim()) {
        try {
          await addComment(p.public_id, notes);
          void qc.invalidateQueries({ queryKey: ['comments', p.public_id] });
        } catch (err) {
          notifications.show({ color: 'red', message: errorMessage(err) });
        }
      }
      handleClose();
      if (navigateTo) navigateTo(p.public_id);
      else await navigate({ to: '/projects/$projectId', params: { projectId: p.public_id } });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('list.newProject')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          {organizations.length > 1 && (
            <Select
              label={t('project.organization')}
              data={organizations.map((o) => ({ value: o.public_id, label: o.name }))}
              value={org}
              onChange={setOrg}
              required
              searchable
            />
          )}
          <TextInput label={t('project.name')} required withAsterisk={false} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput label={t('project.crs')} value={crs} onChange={(e) => setCrs(e.currentTarget.value)} placeholder="EPSG:5514" />
          <Group grow>
            <TextInput label={t('project.acquisitionDate')} type="date" value={acq} onChange={(e) => setAcq(e.currentTarget.value)} />
            <TextInput label={t('project.acquisitionPeriodEnd')} type="date" value={acqEnd} onChange={(e) => setAcqEnd(e.currentTarget.value)} />
          </Group>
          {showInternal && <Textarea label={t('project.firstComment')} description={t('project.firstCommentHint')} maxLength={4000} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />}
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('project.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
